/**
 * Una commessa vista dalle Spedizioni (30-09-2026).
 *
 * Da una domanda di Aida che parla della commessa 916 si deve poter vedere
 * **cosa contiene** senza uscire dal modulo: cliente, consegna, il riferimento
 * con cui la chiama il cliente, le righe con quantità e disponibilità, e le righe
 * del tabellone che la portano già. Legge l'export ordini dall'endpoint di M3,
 * perché a Marina il modulo Commesse non è aperto.
 */

import { X } from "lucide-react";

import { STATO_LABEL } from "@/components/m3/stato-carico";
import { Skeleton } from "@/components/ui/skeleton";
import { useCommessaM3 } from "@/hooks/useM3";
import type { StatoCaricoPrevisto } from "@/types/api";

const n = (v: number | null) =>
  v == null ? "—" : Number.isInteger(v) ? String(v) : v.toLocaleString("it-IT", { maximumFractionDigits: 2 });

export function CommessaDrawer({
  numero,
  onClose,
  onApriRiga,
}: {
  numero: number;
  onClose: () => void;
  /** Apre la riga del tabellone che porta questa commessa. */
  onApriRiga?: (id: string) => void;
}) {
  const { data, isLoading, isError, error } = useCommessaM3(numero);
  return (
    <div className="fixed inset-0 top-14 z-50 flex justify-end" role="dialog">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} aria-hidden />
      <div className="relative z-10 flex h-full w-full max-w-3xl flex-col overflow-y-auto border-l bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="min-w-0">
            <h3 className="font-semibold">Commessa {numero}</h3>
            {data && (
              <p className="truncate text-xs text-muted-foreground">
                {data.cliente_nome}
                {data.riferimento_cliente && <> · per il cliente: «{data.riferimento_cliente}»</>}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-muted" aria-label="Chiudi">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 space-y-4 p-4 text-sm">
          {isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : isError || !data ? (
            <p className="text-sm text-muted-foreground">{(error as Error)?.message ?? "Commessa non trovata."}</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                <Fatto label="Cliente">{data.cliente_nome}</Fatto>
                <Fatto label="Consegna">{new Date(data.data_consegna).toLocaleDateString("it-IT")}</Fatto>
                <Fatto label="Pallet a terra">{n(data.pallet_a_terra)}</Fatto>
                <Fatto label="Stato">{data.stato ?? "—"}</Fatto>
              </div>
              <div>
                <div className="mb-1 text-xs font-semibold">Sul tabellone</div>
                {data.sul_tabellone.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nessuna riga del tabellone porta questa commessa.</p>
                ) : (
                  <ul className="space-y-1">
                    {data.sul_tabellone.map((r) => (
                      <li key={r.id}>
                        <button
                          type="button"
                          disabled={!onApriRiga}
                          onClick={() => onApriRiga?.(r.id)}
                          className="w-full rounded-md border bg-card px-2 py-1 text-left text-xs hover:bg-muted disabled:hover:bg-card"
                        >
                          <b>{r.cliente_nome}</b> · {STATO_LABEL[r.stato as StatoCaricoPrevisto] ?? r.stato}
                          {r.ndp_numero && <> · NDP {r.ndp_numero}</>}
                          {r.data_ritiro && <> · ritiro {new Date(r.data_ritiro).toLocaleDateString("it-IT")}</>}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="mb-1 text-xs font-semibold">Righe ({data.righe.length})</div>
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-xs">
                    <thead className="border-b bg-muted/40 text-left text-muted-foreground">
                      <tr>
                        <th className="px-2 py-1.5 font-medium">Articolo</th>
                        <th className="px-2 py-1.5 text-right font-medium">Ordinata</th>
                        <th className="px-2 py-1.5 text-right font-medium">Spedita</th>
                        <th className="px-2 py-1.5 text-right font-medium">Da spedire</th>
                        <th className="px-2 py-1.5 text-right font-medium">Disponibile</th>
                        <th className="px-2 py-1.5 text-right font-medium">Pallet a terra</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.righe.map((r, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-2 py-1.5">
                            <span className="font-medium tabular-nums">{r.articolo}</span>
                            {r.descrizione && (
                              <span className="block max-w-[22rem] truncate text-[11px] text-muted-foreground">
                                {r.descrizione.replace(/^§\s*/, "")}
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{n(r.ordinata)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{n(r.spedita)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{n(r.residua)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{n(r.disponibile)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{n(r.pallet_a_terra)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Dall'export ordini del {new Date(data.export_del).toLocaleDateString("it-IT")}.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Fatto({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border bg-card p-2">
      <div className="text-[10.5px] text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-medium">{children}</div>
    </div>
  );
}
